import {useMutation} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'

import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {Success, Error} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {ProductListSchemaResponse} from '../domain'
import {useProduct} from '../hook'
import {API} from 'src/feature/bite/domain'
import {useIntl} from 'react-intl'

export function useUpdateProduct() {
  const auth = useAuthUser()
  const {productId} = useParams()
  const {headers, errors} = useRequestParams()

  return useMutation(
    ['ProductUpdate', productId],
    async (values: any) => {
      const Response = await fetch(`${API}/StoreProduct/Update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          ...values,
          userID: auth()?.id,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.product.update_error)

        return
      }

      Success(errors.product.update_success)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.product.update_error)
      },
    }
  )
}

export function useCreateProduct() {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const router = useNavigate()

  const userID = auth()?.id

  return useMutation(
    ['ProductCreate'],
    async (values: any) => {
      const Response = await fetch(`${API}/StoreProduct/Create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          ...values,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.product.create_error)

        return
      }

      Success(errors.product.create_success)

      setTimeout(() => {
        router('/products')
      }, 1000)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.product.create_error)
      },
    }
  )
}

export function useAddCategory() {
  const intl = useIntl()
  const {auth, headers, errors} = useRequestParams()
  const {categories, categoriesSet} = useProduct()

  const userID = auth()?.id

  return useMutation(
    ['CreateCategory'],
    async ({name}: {name: any}) => {
      if (name.length < 3) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_LENGTH_FIELD'}, {length: 3}))
        return null
      }

      if (name.length > 50) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50}))
        return null
      }

      const Response = await fetch(`${API}/Category/AddCategory`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Data || !Data.response.id) {
        Error(Data?.responseResult?.exceptionDetail || errors.category.create_error)
        return null
      }

      Success(errors.category.create_success)

      categoriesSet([
        ...categories,
        {
          id: Data.response.id,
          name: name,
        },
      ])

      return {
        id: Data.response.id,
        name: name,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.category.create_error)
      },
    }
  )
}

export function useAddFamily() {
  const intl = useIntl()
  const {auth, headers, errors} = useRequestParams()
  const {family, familySet} = useProduct()

  const userID = auth()?.id

  return useMutation(
    ['CreateCategory'],
    async ({name}: {name: any}) => {
      if (name.length < 3) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_LENGTH_FIELD'}, {length: 3}))
        return null
      }

      if (name.length > 50) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50}))
        return null
      }

      const Response = await fetch(`${API}/StoreProductFamily/Create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Data || !Data.response.id) {
        Error(Data?.responseResult?.exceptionDetail || errors.category.create_error)
        return null
      }

      Success(errors.category.create_success)

      familySet([
        ...family,
        {
          id: Data.response.id,
          name: name,
        },
      ])

      return {
        id: Data.response.id,
        name: name,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.category.create_error)
      },
    }
  )
}

export function useGetAll() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {list} = useProduct()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {
      currentPageNumber: 1,
      pageSize: list.paging.totalCount,
    },
  }

  return useMutation(
    ['productsAll'],
    async () => {
      const Response = await fetch(`${API}/StoreProduct/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = ProductListSchemaResponse.safeParse(Data)
      const parse = ProductListSchemaResponse.parse(Data)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return []
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      return parse.response.dataList
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}
